import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@narmi/design_system";
import { ContentCard, useLoadingContext } from "cerulean";

import Account from "byzantine/src/Account";
import cc from "classcat";
import { useLocalization } from "@fluent/react";

import { entities, ComposeProviders, modules } from "byzantine";
import { CustomAlertPayload } from "byzantine/src/dbbl/businessLogic/modules/alerts/custom";
import { useTriggerGuideForceReset } from "../../../../src/modules/guides/hooks";
import { NewAlertDialog, AlertsList, NoAlerts } from "./components";

import styles from "./CustomAlerts.module.scss";

interface CustomAlertsProps {
  accounts: Account[];
}

const CustomAlerts = ({ accounts }: CustomAlertsProps) => {
  const { l10n } = useLocalization();
  const { setIsLoading } = useLoadingContext();
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const triggerGuideForceReset = useTriggerGuideForceReset();

  const { send: fetchAlertsRequest, customAlerts } =
    entities.customAlerts.useGetCustomAlerts();
  const { send: deleteAlertRequest } =
    entities.customAlerts.useDeleteCustomAlert();
  const { send: createAlertRequest } =
    entities.customAlerts.useCreateCustomAlert();

  const Providers = [modules.customAlerts.customAlertForm.Provider];

  const deleteAlert = useCallback(
    (alertId: API.SavedSearchId, onFinally?: () => void) => {
      deleteAlertRequest(alertId, {
        onFinally,
      });
    },
    [deleteAlertRequest]
  );

  const createAlert = useCallback(
    (payload: CustomAlertPayload, onFinally?: () => void) => {
      createAlertRequest(payload, {
        onSuccess() {
          triggerGuideForceReset();
        },
        onFinally() {
          onFinally?.();
          setDialogIsOpen(false);
        },
      });
    },
    [createAlertRequest, triggerGuideForceReset]
  );

  useEffect(() => {
    setIsLoading(true);
    fetchAlertsRequest({
      onFinally() {
        setIsLoading(false);
      },
    });
  }, []);

  return (
    <div className={styles.container}>
      <header>
        <h4
          className={cc([
            "fontSize--heading4 fontFamily--default",
            styles.heading,
          ])}
        >
          {l10n.getString("custom-alerts-title", {}, "Custom alerts")}
        </h4>
      </header>
      <p className={cc(["fontFamily--default", styles.description])}>
        {l10n.getString(
          "custom-alerts-sub-text",
          {},
          "Keep your accounts safe and manage them more efficiently."
        )}
      </p>
      <ContentCard kind="bordered" style={styles.contentCard}>
        {customAlerts.length > 0 ? (
          <AlertsList
            accounts={accounts}
            deleteAlert={deleteAlert}
            alerts={customAlerts}
          />
        ) : (
          <NoAlerts fetchAlerts={fetchAlertsRequest} setIsLoading={setIsLoading} />
        )}
        <div className={styles.alertButtonContainer}>
          <Button
            kind="secondary"
            startIcon="plus"
            onClick={() => setDialogIsOpen(true)}
            style={{ textWrap: "nowrap" }}
          >
            {l10n.getString(
              "custom-alerts-button-create",
              {},
              "Add a new custom alert"
            )}
          </Button>
        </div>
      </ContentCard>

      <ComposeProviders components={Providers}>
        <NewAlertDialog
          accounts={accounts}
          dialogIsOpen={dialogIsOpen}
          onCreateAlert={createAlert}
          setDialogIsOpen={setDialogIsOpen}
        />
      </ComposeProviders>
    </div>
  );
};

export default CustomAlerts;
